import { BaseService } from './base.service';
import { CategoryService } from './category.service';

export class ProductService extends BaseService {
  constructor(categoryService = null) {
    super('products');
    this._categoryService = categoryService;
  }

  // Getter pour categoryService avec initialisation différée
  get categoryService() {
    if (!this._categoryService) {
      this._categoryService = new CategoryService(this);
    }
    return this._categoryService;
  }

  /**
   * Crée un nouveau produit
   * @param {Object} product - Données du produit
   * @returns {Promise<Object>} - Produit créé
   */
  async create(product) {
    try {
      // Récupérer le nom de la catégorie
      const category = await this.categoryService.getById(product.categoryId);
      if (!category) {
        throw new Error('Catégorie non trouvée');
      }

      // Ajouter le nom de la catégorie au produit
      const productWithCategory = {
        ...product,
        categoryName: category.name
      };

      return await super.create(productWithCategory);
    } catch (error) {
      console.error('Erreur lors de la création du produit:', error);
      throw error;
    }
  }

  /**
   * Récupère les produits d'une catégorie
   * @param {number} categoryId - ID de la catégorie
   * @returns {Promise<Array>} - Liste des produits de la catégorie
   */
  async getByCategory(categoryId) {
    const products = await this.getByField('categoryId', categoryId);
    // console.log('products', products);
    return products;
  }

  /**
   * Récupère les produits actifs
   * @returns {Promise<Array>} - Liste des produits actifs
   */
  async getActiveProducts() {
    return this.getByField('isActive', true);
  }

  /**
   * Met à jour un produit et synchronise avec sa catégorie
   * @param {number} productId - ID du produit
   * @param {Object} updates - Données à mettre à jour
   * @returns {Promise<Object>} - Produit mis à jour
   */
  async updateProduct(productId, updates) {
    try {
      // Si categoryId est modifié, récupérer le nouveau nom de catégorie
      if (updates.categoryId) {
        const category = await this.categoryService.getById(updates.categoryId);
        if (!category) {
          throw new Error('Catégorie non trouvée');
        }
        updates.categoryName = category.name;
      }

      // Mise à jour du produit
      const updatedProduct = await this.update(productId, updates);
      return updatedProduct;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du produit:', error);
      throw error;
    }
  }

  /**
   * Met à jour le stock d'un produit
   * @param {number} productId - ID du produit
   * @param {number} quantity - Quantité à ajouter (positive) ou retirer (négative)
   * @returns {Promise<Object>} - Le produit mis à jour
   */
  async updateStock(productId, quantity) {
    const product = await this.getById(productId);
    if (!product) throw new Error('Product not found');
    
    return this.update(productId, {
      stock: product.stock + quantity
    });
  }

  /**
   * Recherche des produits
   * @param {string} query - Terme de recherche
   * @returns {Promise<Array>} - Liste des produits correspondants
   */
  async searchProducts(query) {
    return this.search(query, ['name', 'description', 'barcode', 'categoryName']);
  }

  /**
   * Récupère les produits avec un stock bas
   * @param {number} threshold - Seuil de stock bas
   * @returns {Promise<Array>} - Liste des produits avec stock bas
   */
  async getProductsWithLowStock(threshold = 5) {
    return this.table
      .where('stock')
      .belowOrEqual(threshold)
      .and(item => item.isActive)
      .toArray();
  }

  /**
   * Récupère un produit avec les informations de sa catégorie
   * @param {number} productId - ID du produit
   * @returns {Promise<Object>} - Le produit avec les informations de sa catégorie
   */
  async getProductWithCategory(productId) {
    try {
      const product = await this.getById(productId);
      if (!product) return null;

      const category = await this.categoryService.getById(product.categoryId);
      return {
        ...product,
        category
      };
    } catch (error) {
      console.error('Erreur lors de la récupération du produit:', error);
      throw error;
    }
  }

  /**
   * Récupère tous les produits avec les informations de leurs catégories
   * @returns {Promise<Array>} - Liste des produits avec leurs catégories
   */
  async getAllProductsWithCategories() {
    try {
      const products = await this.getAll();
      const productsWithCategories = await Promise.all(
        products.map(async (product) => {
          const category = await this.categoryService.getById(product.categoryId);
          return {
            ...product,
            category
          };
        })
      );
      return productsWithCategories;
    } catch (error) {
      console.error('Erreur lors de la récupération des produits:', error);
      throw error;
    }
  }

  /**
   * Vérifie si un produit existe dans une catégorie
   * @param {number} categoryId - ID de la catégorie
   * @returns {Promise<boolean>} - true si des produits existent dans la catégorie
   */
  async hasProductsInCategory(categoryId) {
    try {
      const count = await this.table
        .where('categoryId')
        .equals(categoryId)
        .count();
      return count > 0;
    } catch (error) {
      console.error('Erreur lors de la vérification des produits:', error);
      throw error;
    }
  }

  /**
   * Met à jour le nom de catégorie pour un produit
   * @param {number} productId - ID du produit
   * @param {string} categoryName - Nouveau nom de catégorie
   * @returns {Promise<Object>} - Produit mis à jour
   */
  async updateCategoryName(productId, categoryName) {
    try {
      return await this.update(productId, { categoryName });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du nom de catégorie:', error);
      throw error;
    }
  }

  /**
   * Met à jour le nom de catégorie pour tous les produits d'une catégorie
   * @param {number} categoryId - ID de la catégorie
   * @param {string} categoryName - Nouveau nom de catégorie
   * @returns {Promise<Array>} - Produits mis à jour
   */
  async updateCategoryNameForAllProducts(categoryId, categoryName) {
    // console.log('updateCategoryNameForAllProducts', categoryId, categoryName);
    try {
      // Récupérer tous les produits de la catégorie
      const products = await this.getByCategory(categoryId);
      // console.log('products', products);
      // Mettre à jour chaque produit avec le nouveau nom de catégorie
      const updates = products.map(product => ({
        ...product,
        categoryName: categoryName,
        updatedAt: new Date().toISOString()
      }));

      // Mettre à jour tous les produits en une seule opération
      await this.table.bulkPut(updates);
      
      return updates;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des noms de catégorie:', error);
      throw error;
    }
  }
} 